import React, {useMemo, useRef, useState} from "react"
import PropTypes from "prop-types"
import dayjs from "dayjs"
import BuildRoundedIcon from "@mui/icons-material/BuildRounded"
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded"
import {Box, Grid, Paper, Stack, Typography} from "@mui/material"
import ButtonWithIcon from "src/v2/components/Button/Button"
import DatePickerSelectV2 from "src/v2/components/Date/date-picker-select"
import TextFieldInput from "src/v2/components/Input/text-field-input"
import SelectComponent from "src/v2/components/select/select-component"
import UploadComponent from "src/v2/components/upload/upload-component"

const DEFAULT_VALUES = {
    date: dayjs().format("YYYY-MM-DD"),
    endDate: dayjs().format("YYYY-MM-DD"),
    days: 90,
    cetipList: "",
    cetip: "",
    calculationType: "",
    fileType: "",
    file: null,
}

const RoutineExecutionForm = ({
                                  routines,
                                  loading,
                                  onSubmit,
                                  onRebuild,
                                  onValidationError,
                              }) => {
    const [routineValue, setRoutineValue] = useState("")
    const [values, setValues] = useState(DEFAULT_VALUES)
    const fileInputRef = useRef(null)
    const definition = useMemo(
        () => routines.find((routine) => routine.value === routineValue),
        [routineValue, routines]
    )

    const setValue = (name, value) => setValues((current) => ({...current, [name]: value}))

    const changeDate = (name) => (value) => {
        if (value && dayjs(value).isValid()) setValue(name, dayjs(value).format("YYYY-MM-DD"))
    }

    const handleFile = (file) => {
        const extension = file?.name?.split(".").pop()?.toLowerCase() || ""
        setValues((current) => ({
            ...current,
            file,
            fileType: ["csv", "json"].includes(extension) ? extension : current.fileType,
        }))
    }

    const validate = () => {
        if (!definition || !values.date) return "Selecione uma rotina e uma data válida"
        if (definition.fields.file && (!values.file || !values.fileType)) {
            return "Selecione um arquivo CSV ou JSON válido"
        }
        if (definition.fields.endDate && !values.endDate) return "Selecione a data final"
        return null
    }

    const handleSubmit = () => {
        const validationError = validate()
        if (validationError) {
            onValidationError(validationError)
            return
        }
        onSubmit(definition, values)
    }

    return (
        <Paper sx={{p: 3, backgroundColor: "background.paper"}}>

            <Stack
                direction={{xs: "column", sm: "row"}}
                spacing={2}
                alignItems={{xs: "stretch", sm: "center"}}
                justifyContent="space-between"
            >
                <Box>
                    <Typography variant="h3" color="common.white">Executar rotina manualmente</Typography>
                    <Typography variant="body2" color="primary.white70" sx={{mt: 0.5}}>
                        Selecione a rotina e informe somente os parâmetros necessários.
                    </Typography>
                </Box>

                <Stack
                    direction={{xs: "column", sm: "row"}}
                    spacing={1.5}
                    sx={{flexShrink: 0}}
                >
                    <ButtonWithIcon
                        size="md"
                        icon={<PlayArrowRoundedIcon/>}
                        onClick={handleSubmit}
                        disabled={loading}
                    >
                        INICIAR ROTINA
                    </ButtonWithIcon>
                    {definition?.allowRebuild && (
                        <ButtonWithIcon
                            size="md"
                            icon={<BuildRoundedIcon/>}
                            onClick={onRebuild}
                            disabled={loading}
                        >
                            REBUILD INDEX
                        </ButtonWithIcon>
                    )}
                </Stack>
            </Stack>

            <Box sx={{mt: 4}}></Box>
            <Grid container direction="row" alignItems="flex-start" spacing={2}>
                <Grid item xs={12} md={4}>
                    <SelectComponent
                        label="Rotina"
                        value={routineValue}
                        onChange={(event) => {
                            setRoutineValue(event.target.value)
                            setValues((current) => ({
                                ...current,
                                file: null,
                                fileType: "",
                                cetipList: "",
                                cetip: "",
                                calculationType: "",
                            }))
                        }}
                        options={routines.map((routine) => ({value: routine.value, label: routine.label}))}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                    <DatePickerSelectV2 size={"sm"} label="Data de referência" value={values.date}
                                        onChange={changeDate("date")}/>
                </Grid>

                {definition?.fields.endDate && (
                    <Grid item xs={12} sm={6} md={2}>
                        <DatePickerSelectV2 size={"sm"} label="Data final" value={values.endDate}
                                            onChange={changeDate("endDate")}/>
                    </Grid>
                )}
                {definition?.fields.days && (
                    <Grid item xs={12} sm={6} md={2}>
                        <TextFieldInput
                            type="number"
                            label="Quantidade de dias"
                            value={values.days}
                            onChange={(event) => setValue("days", event.target.value)}
                            inputProps={{min: 1}}
                            fullWidth
                        />
                    </Grid>
                )}
                {definition?.fields.cetipList && (
                    <Grid item xs={12} md={8}>
                        <TextFieldInput
                            label="CETIPs separados por vírgula"
                            value={values.cetipList}
                            onChange={(event) => setValue("cetipList", event.target.value)}
                            fullWidth
                        />
                    </Grid>
                )}
                {definition?.fields.calculation && (
                    <Grid item xs={12} sm={6} md={2}>
                        <SelectComponent
                            label="Cálculo"
                            value={values.calculationType}
                            onChange={(event) => setValue("calculationType", event.target.value)}
                            options={[
                                {value: "PUPAR", label: "PUPAR"},
                                {value: "PUMTM", label: "PUMTM"},
                            ]}
                        />
                    </Grid>
                )}
                {definition?.fields.cetip && (
                    <Grid item xs={12} sm={6} md={2}>
                        <TextFieldInput
                            label="CETIP"
                            value={values.cetip}
                            onChange={(event) => setValue("cetip", event.target.value)}
                            fullWidth
                        />
                    </Grid>
                )}
                {definition?.fields.file && (
                    <>
                        <Grid item xs={12} sm={4} md={2}>
                            <SelectComponent
                                label="Tipo do arquivo"
                                value={values.fileType}
                                onChange={(event) => setValue("fileType", event.target.value)}
                                options={[
                                    {value: "csv", label: "CSV"},
                                    {value: "json", label: "JSON"},
                                ]}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <UploadComponent
                                compact
                                accept=".csv,.json"
                                buttonLabel="SELECIONAR"
                                helperText="Arraste o CSV ou JSON aqui"
                                fileInputRef={fileInputRef}
                                onFileSelect={handleFile}
                                isSending={loading}
                            />
                        </Grid>
                    </>
                )}
            </Grid>
        </Paper>
    )
}

const routineShape = PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    endpoint: PropTypes.string,
    fields: PropTypes.object.isRequired,
    allowRebuild: PropTypes.bool,
    execute: PropTypes.func.isRequired,
})

RoutineExecutionForm.propTypes = {
    routines: PropTypes.arrayOf(routineShape).isRequired,
    loading: PropTypes.bool,
    onSubmit: PropTypes.func.isRequired,
    onRebuild: PropTypes.func.isRequired,
    onValidationError: PropTypes.func.isRequired,
}

export default RoutineExecutionForm
